import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Button,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import {
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import './TermsPage.css';

const TermsPage = () => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showDownloadAlert, setShowDownloadAlert] = useState(false);

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    setTimeout(() => setTermsAccepted(false), 3000);
  };

  const handleDownloadPDF = () => {
    setShowDownloadAlert(true);
    setTimeout(() => setShowDownloadAlert(false), 3000);
  };

  return (
    <div className="terms-page">
      <Helmet>
        <title>Conditions Générales - Mission Sport</title>
        <meta 
          name="description" 
          content="Consultez les conditions générales d'utilisation de Mission Sport, la plateforme de mise en relation entre organisations sportives et professionnels du sport."
        />
        <meta name="keywords" content="conditions générales, CGU, Mission Sport, plateforme sportive, termes utilisation" />
        <meta property="og:title" content="Conditions Générales - Mission Sport" />
        <meta property="og:description" content="Consultez les conditions générales d'utilisation de Mission Sport, la plateforme de mise en relation entre organisations sportives et professionnels du sport." />
        <meta property="og:url" content="https://missionsport.fr/terms" />
        <meta property="og:type" content="website" />
      </Helmet>

      <Container maxWidth="lg" className="terms-container">
        {/* Breadcrumb */}
        <Box className="breadcrumb-section">
          <Breadcrumbs aria-label="breadcrumb">
            <Link 
              component={RouterLink} 
              to="/" 
              color="inherit" 
              className="breadcrumb-link"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Accueil
            </Link>
            <Typography color="text.primary">Conditions générales</Typography>
          </Breadcrumbs>
        </Box>

        {/* Header */}
        <Box className="terms-header">
          <Typography variant="h1" className="terms-title">
            Conditions Générales d'Utilisation
          </Typography>
          <Typography variant="h2" className="terms-subtitle">
            Les règles qui protègent nos utilisateurs et encadrent nos services
          </Typography>
          <Typography variant="body1" className="terms-last-update">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box className="terms-actions">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            className="download-btn"
          >
            Télécharger en PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircleIcon />}
            onClick={handleAcceptTerms}
            className="accept-btn"
            disabled={termsAccepted}
          >
            {termsAccepted ? 'CGU Acceptées' : 'Accepter les CGU'}
          </Button>
        </Box>

        {/* Alerts */}
        {showDownloadAlert && (
          <Alert severity="info" className="terms-alert">
            Fonctionnalité de téléchargement PDF en cours de développement.
          </Alert>
        )}
        {termsAccepted && (
          <Alert severity="success" className="terms-alert">
            Merci d'avoir accepté nos conditions générales d'utilisation.
          </Alert>
        )}

        {/* Content */}
        <Paper className="terms-content" elevation={1}>
          {/* Introduction */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              1. Introduction
            </Typography>
            <Typography variant="body1" className="section-text">
              Bienvenue sur Mission Sport, la plateforme innovante qui révolutionne la mise en relation 
              entre les organisations sportives et les professionnels indépendants du sport. Ces conditions 
              générales d'utilisation ("CGU") définissent les règles d'utilisation de notre plateforme et 
              garantissent une expérience sécurisée et transparente pour tous nos utilisateurs.
            </Typography>
            <Typography variant="body1" className="section-text">
              En utilisant Mission Sport, vous acceptez pleinement et sans réserve les présentes conditions 
              générales d'utilisation. Si vous n'acceptez pas ces conditions, nous vous invitons à ne pas 
              utiliser notre plateforme.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Objet de la plateforme */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              2. Objet de la plateforme
            </Typography>
            <Typography variant="body1" className="section-text">
              Mission Sport est une plateforme numérique qui facilite la mise en relation entre :
            </Typography>
            <ul className="terms-list">
              <li>Les organisations sportives (clubs, fédérations, associations, entreprises) recherchant des compétences spécialisées</li>
              <li>Les professionnels indépendants du sport (coachs, préparateurs physiques, nutritionnistes, kinésithérapeutes, etc.)</li>
            </ul>
            <Typography variant="body1" className="section-text">
              Notre mission est de démocratiser l'accès aux services sportifs de qualité tout en offrant 
              aux professionnels une visibilité accrue et des opportunités de développement.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Inscription et accès */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              3. Inscription et accès aux services
            </Typography>
            <Typography variant="h4" className="subsection-title">
              3.1 Création de compte
            </Typography>
            <Typography variant="body1" className="section-text">
              L'inscription sur Mission Sport est gratuite et ouverte à toute personne physique ou morale 
              majeure. Vous vous engagez à fournir des informations exactes, complètes et à jour lors de 
              votre inscription.
            </Typography>
            <Typography variant="h4" className="subsection-title">
              3.2 Profils utilisateurs
            </Typography>
            <Typography variant="body1" className="section-text">
              Chaque utilisateur est responsable de la véracité des informations présentes sur son profil. 
              Les professionnels doivent justifier de leurs qualifications et certifications. Mission Sport 
              se réserve le droit de vérifier ces informations.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Utilisation des services */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              4. Utilisation des services
            </Typography>
            <Typography variant="h4" className="subsection-title">
              4.1 Obligations des organisations
            </Typography>
            <ul className="terms-list">
              <li>Publier des offres de mission claires et détaillées</li>
              <li>Respecter les tarifs convenus avec les professionnels</li>
              <li>Fournir un environnement de travail sécurisé</li>
              <li>Respecter la charte de bonne conduite de la plateforme</li>
            </ul>
            <Typography variant="h4" className="subsection-title">
              4.2 Obligations des professionnels
            </Typography>
            <ul className="terms-list">
              <li>Maintenir leurs qualifications et certifications à jour</li>
              <li>Respecter les engagements pris auprès des organisations</li>
              <li>Fournir des services de qualité professionnelle</li>
              <li>Respecter la confidentialité des informations clients</li>
            </ul>
          </section>

          <Divider className="section-divider" />

          {/* Paiements et facturation */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              5. Paiements et facturation
            </Typography>
            <Typography variant="body1" className="section-text">
              Les paiements sur Mission Sport sont sécurisés par nos partenaires de confiance Stripe et PayPal. 
              Mission Sport prélève une commission sur chaque transaction réussie selon les tarifs en vigueur.
            </Typography>
            <Typography variant="body1" className="section-text">
              Les professionnels sont responsables de leurs obligations fiscales et sociales. Mission Sport 
              fournit les justificatifs nécessaires mais ne se substitue pas aux obligations légales de chacun.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Propriété intellectuelle */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              6. Propriété intellectuelle
            </Typography>
            <Typography variant="body1" className="section-text">
              Tous les contenus présents sur Mission Sport (textes, images, logos, vidéos) sont protégés 
              par les droits de propriété intellectuelle. Les utilisateurs conservent leurs droits sur les 
              contenus qu'ils publient mais accordent à Mission Sport une licence d'utilisation dans le 
              cadre du fonctionnement de la plateforme.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Données personnelles */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              7. Données personnelles et confidentialité
            </Typography>
            <Typography variant="body1" className="section-text">
              Mission Sport s'engage à protéger vos données personnelles conformément au Règlement Général 
              sur la Protection des Données (RGPD). Le traitement de vos données est détaillé dans notre 
              <Link component={RouterLink} to="/privacy" className="inline-link">
                Politique de confidentialité
              </Link>.
            </Typography>
            <Typography variant="body1" className="section-text">
              Vous disposez d'un droit d'accès, de rectification, de suppression et de portabilité de vos 
              données personnelles.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Responsabilités */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              8. Responsabilités
            </Typography>
            <Typography variant="h4" className="subsection-title">
              8.1 Responsabilité de Mission Sport
            </Typography>
            <Typography variant="body1" className="section-text">
              Mission Sport s'engage à fournir une plateforme fonctionnelle et sécurisée. Cependant, 
              Mission Sport ne peut être tenue responsable des relations contractuelles entre les 
              utilisateurs, de la qualité des services fournis ou des dommages indirects.
            </Typography>
            <Typography variant="h4" className="subsection-title">
              8.2 Responsabilité des utilisateurs
            </Typography>
            <Typography variant="body1" className="section-text">
              Chaque utilisateur est responsable de ses actes sur la plateforme et des conséquences de 
              ses relations avec les autres utilisateurs. Les utilisateurs s'engagent à respecter la 
              législation en vigueur.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Suspension et résiliation */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              9. Suspension et résiliation
            </Typography>
            <Typography variant="body1" className="section-text">
              Mission Sport se réserve le droit de suspendre ou de résilier l'accès à la plateforme en 
              cas de non-respect des présentes conditions générales, de comportement inapproprié ou 
              d'activité frauduleuse.
            </Typography>
            <Typography variant="body1" className="section-text">
              Les utilisateurs peuvent résilier leur compte à tout moment en nous contactant. La 
              résiliation n'affecte pas les obligations en cours.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Modifications */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              10. Modifications des conditions
            </Typography>
            <Typography variant="body1" className="section-text">
              Mission Sport se réserve le droit de modifier les présentes conditions générales à tout 
              moment. Les utilisateurs seront informés des modifications par email et/ou notification 
              sur la plateforme. La poursuite de l'utilisation après notification vaut acceptation des 
              nouvelles conditions.
            </Typography>
          </section>

          <Divider className="section-divider" />

          {/* Contact */}
          <section className="terms-section">
            <Typography variant="h3" className="section-title">
              11. Contact
            </Typography>
            <Typography variant="body1" className="section-text">
              Pour toute question relative aux présentes conditions générales d'utilisation, vous pouvez 
              nous contacter :
            </Typography>
            <ul className="terms-list">
              <li>Par email : <a href="mailto:legal@missionsport.fr" className="inline-link">legal@missionsport.fr</a></li>
              <li>Via notre <Link component={RouterLink} to="/contact" className="inline-link">formulaire de contact</Link></li>
              <li>Par courrier : Mission Sport - Service Juridique, [Adresse à définir]</li>
            </ul>
          </section>
        </Paper>

        {/* Footer Actions */}
        <Box className="terms-footer-actions">
          <Button
            variant="contained"
            size="large"
            startIcon={<CheckCircleIcon />}
            onClick={handleAcceptTerms}
            className="accept-btn-footer"
            disabled={termsAccepted}
          >
            {termsAccepted ? 'CGU Acceptées ✓' : 'J\'accepte les conditions générales'}
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default TermsPage;